import React from 'react';
import { useRecoilValue } from 'recoil';
import { SvgIcon } from '@ui-elements';
import { positionalCtaIcon, resolveOfferCta } from '../../cta-template';
import { OfferCtaLinks } from '../../store';
import styles from './index.module.scss';

/**
 * The row of secondary links under the main call to action.
 *
 * A CTA an editor picked from the presets says which one it is, and cta-template.ts decides its wording, its
 * destination and its icon from that. Anything else - which is every CTA authored to date - keeps rendering
 * the wording and the destination stored on the offer, with an icon chosen by its place in the row.
 *
 * Both rules live in cta-template.ts rather than here, because the same three icons at the same three sizes
 * are now reachable two ways and a test can only hold them to the design from a module with no React in it.
 */

const ICON_COLOUR = '#043A9C';

export const OfferSecondaryCtas: React.FC = () => {
  const secondaryCtas = useRecoilValue(OfferCtaLinks);

  if (secondaryCtas.length === 0) return null;

  return (
    <div className={styles.secondaryCtas} data-testid="offer-secondary-ctas">
      {secondaryCtas.map((content, index) => {
        if (content.contentType !== 'CTA') return null;

        const cta = resolveOfferCta(content.content);
        // A preset brings its own icon; only a CTA without one still depends on where it sits in the row.
        const icon = cta.icon ?? positionalCtaIcon(index);

        return (
          <a
            key={`${cta.href}-${index}`}
            href={cta.href}
            className={styles.secondaryCta}
            data-testid={`offer-secondary-cta-${index}`}
          >
            <SvgIcon
              type={icon.type}
              // em, so the icons track the link's own type size rather than being pinned to pixels.
              size={icon.size}
              color={ICON_COLOUR}
            />
            <span>{cta.label}</span>
          </a>
        );
      })}
    </div>
  );
};
